import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import './Pagination.css';

/**
 * Reusable pagination controls.
 *
 * Props:
 *   page        - Current page (1-based)
 *   totalPages  - Total number of pages
 *   total       - Total record count
 *   limit       - Rows per page
 *   onPageChange(page)  - Callback when page changes
 *   onLimitChange(limit) - Callback when rows-per-page changes
 */
export default function Pagination({ page, totalPages, total, limit, onPageChange, onLimitChange }) {
  if (total === 0) return null;

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  // Generate page numbers to display (max 7 buttons)
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push('...');
      const rangeStart = Math.max(2, page - 1);
      const rangeEnd = Math.min(totalPages - 1, page + 1);
      for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
      if (page < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <nav className="pagination-container" aria-label="Pagination">
      <div className="pagination-info" aria-live="polite">
        Showing {start}-{end} of {total.toLocaleString()} results
      </div>

      <div className="pagination-controls">
        <div className="pagination-limit">
          <label htmlFor="pagination-rows-select">Rows:</label>
          <select
            id="pagination-rows-select"
            value={limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
            className="pagination-limit-select"
            aria-label="Rows per page"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        <div className="pagination-buttons">
          <button
            className="pagination-btn"
            onClick={() => onPageChange(1)}
            disabled={page === 1}
            title="First page"
            aria-label="First page"
          >
            <ChevronsLeft size={16} />
          </button>
          <button
            className="pagination-btn"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
            title="Previous page"
            aria-label="Previous page"
          >
            <ChevronLeft size={16} />
          </button>

          {getPageNumbers().map((p, i) =>
            p === '...' ? (
              <span key={`ellipsis-${i}`} className="pagination-ellipsis" aria-hidden="true">...</span>
            ) : (
              <button
                key={p}
                className={`pagination-btn pagination-page ${p === page ? 'active' : ''}`}
                onClick={() => onPageChange(p)}
                aria-label={`Page ${p}`}
                aria-current={p === page ? 'page' : undefined}
              >
                {p}
              </button>
            )
          )}

          <button
            className="pagination-btn"
            onClick={() => onPageChange(page + 1)}
            disabled={page === totalPages}
            title="Next page"
            aria-label="Next page"
          >
            <ChevronRight size={16} />
          </button>
          <button
            className="pagination-btn"
            onClick={() => onPageChange(totalPages)}
            disabled={page === totalPages}
            title="Last page"
            aria-label="Last page"
          >
            <ChevronsRight size={16} />
          </button>
        </div>
      </div>
    </nav>
  );
}
